/* eslint-disable react/jsx-no-bind */
import { themeGet } from '@styled-system/theme-get';
import { Carousel } from 'pcln-carousel';
import { Box, Flex, Image, Link, Relative, Text, theme } from 'pcln-design-system';
import { ArrowRight } from 'pcln-icons';
import React, { useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import styled from 'styled-components';
import HeroItem from './HeroItem';
const Wrapper = /*#__PURE__*/styled(Flex).withConfig({
  displayName: "HeroCollection__Wrapper",
  componentId: "sc-1ueexg0-0"
})(["max-width:1232px;width:100%;gap:16px;& div.carousel__slide{width:auto !important;}"]);
const StyledImage = /*#__PURE__*/styled(Image).withConfig({
  displayName: "HeroCollection__StyledImage",
  componentId: "sc-1ueexg0-1"
})(["object-fit:cover;"]);
const GradientOverlay = /*#__PURE__*/styled.div.withConfig({
  displayName: "HeroCollection__GradientOverlay",
  componentId: "sc-1ueexg0-2"
})(["position:absolute;inset:0;border-radius:12px;pointer-events:none;&:after{content:'';position:absolute;border-radius:12px;inset:0;background:linear-gradient( 180deg,rgba(0,0,0,0) 45.97%,rgba(0,0,0,0.4) 62.18%,rgba(0,0,0,0.6) 78.39% );transition:background 0.2s ease-in-out;}"]);
const HeroImageContainer = /*#__PURE__*/styled(Box).withConfig({
  displayName: "HeroCollection__HeroImageContainer",
  componentId: "sc-1ueexg0-3"
})(["position:relative;&:hover ", ":after{background:", ";}"], GradientOverlay, ({
  isLink
}) => isLink ? 'linear-gradient(180deg, rgba(0, 0, 0, 0) 45.97%, rgba(0, 0, 0, 0.5) 62.18%, rgba(0, 0, 0, 0.7) 78.39%)' : 'linear-gradient(180deg, rgba(0, 0, 0, 0) 45.97%, rgba(0, 0, 0, 0.4) 62.18%, rgba(0, 0, 0, 0.6) 78.39%)');
const HeroImageTextWrapper = /*#__PURE__*/styled(Flex).withConfig({
  displayName: "HeroCollection__HeroImageTextWrapper",
  componentId: "sc-1ueexg0-4"
})(["position:absolute;bottom:12%;gap:4px;padding:16px;width:100%;"]);
const AdHighlight = /*#__PURE__*/styled(Box).withConfig({
  displayName: "HeroCollection__AdHighlight",
  componentId: "sc-1ueexg0-5"
})(["position:absolute;right:20px;top:20px;"]);
const ClickableOverlay = /*#__PURE__*/styled.button.withConfig({
  displayName: "HeroCollection__ClickableOverlay",
  componentId: "sc-1ueexg0-6"
})(["position:absolute;inset:0 0 0 0;cursor:pointer;border:none;background:transparent;padding:0;transition:background-color 0.2s ease-in-out;&:hover{background-color:rgba(0,0,0,0.05);}&:focus{outline:2px solid ", ";outline-offset:2px;}"], themeGet('colors.primary'));
/** @public */
export default function HeroCollection({
  heroCollection,
  handleSelect,
  overflowAllowancePxX = 0,
  shouldLazyLoadHeroImage = false
}) {
  const {
    collectionHeading,
    collectionLink,
    collectionSubheading,
    heroImage,
    heroHeading,
    heroBody,
    heroLink,
    collection,
    showAdHighlight
  } = heroCollection;
  const isDesktop = useMediaQuery({
    query: `(min-width: ${theme.breakpoints.md})`
  });
  // isMounted is used to block server side rendering
  // for sections that are known to cause React Hydration errors
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);
  return React.createElement(Wrapper, {
    "data-testid": 'hero-collection',
    flexDirection: 'column'
  }, collectionHeading && React.createElement(Flex, {
    flexDirection: 'column',
    style: {
      gap: '4px'
    }
  }, React.createElement(Flex, {
    alignItems: 'center',
    style: {
      gap: '24px'
    }
  }, React.createElement(Text, {
    textStyle: 'heading3',
    color: 'text.heading'
  }, collectionHeading), collectionLink && isDesktop && isMounted && React.createElement(Link, {
    href: collectionLink.url,
    target: collectionLink.openInNewTab ? '_blank' : '_self',
    onClick: () => {
      handleSelect(collectionLink.analyticsTracking);
    },
    "aria-label": collectionLink.ariaLabel
  }, React.createElement(Flex, {
    alignItems: 'center',
    style: {
      gap: '8px'
    },
    pt: 1
  }, React.createElement(Text, {
    bold: true,
    fontSize: 1,
    color: 'primary'
  }, collectionLink.label), React.createElement(ArrowRight, null)))), collectionSubheading && React.createElement(Text, {
    textStyle: 'subheading5',
    color: 'text.heading'
  }, collectionSubheading)), heroImage && React.createElement(HeroImageContainer, {
    isLink: !!heroLink,
    borderRadius: 'lg'
  }, React.createElement(Relative, null, React.createElement(StyledImage, {
    alt: heroImage.altText,
    loading: shouldLazyLoadHeroImage ? 'lazy' : 'eager',
    src: `${heroImage.asset?.file?.url}?fm=webp&w=1232&q=90`,
    width: '100%',
    height: '364px',
    borderRadius: 'lg'
  }), showAdHighlight && React.createElement(AdHighlight, {
    width: 19,
    height: 16,
    bg: 'background',
    borderRadius: 'xsm'
  }, React.createElement(Text, {
    textStyle: 'disclaimer',
    textAlign: 'center',
    color: 'text'
  }, "Ad"))), React.createElement(GradientOverlay, {
    isLink: !!heroLink
  }), React.createElement(HeroImageTextWrapper, {
    width: 'fit-content',
    flexDirection: 'column',
    px: 3
  }, heroHeading && React.createElement(Text, {
    textStyle: 'heading3',
    color: 'text.lightest'
  }, heroHeading), React.createElement(Flex, {
    flexDirection: ['column', null, 'row'],
    alignItems: ['flex-start', null, 'center'],
    style: {
      columnGap: '24px',
      rowGap: '8px'
    }
  }, heroBody && React.createElement(Text, {
    textStyle: 'subheading5',
    color: 'text.lightest'
  }, heroBody), heroLink && React.createElement(Link, {
    href: heroLink.url,
    target: heroLink.openInNewTab ? '_blank' : '_self',
    "aria-label": heroLink.ariaLabel,
    onClick: () => {
      handleSelect(heroLink.analyticsTracking);
    }
  }, React.createElement(Flex, {
    alignItems: 'center'
  }, React.createElement(Text, {
    textStyle: 'paragraphBold',
    color: 'text.lightest'
  }, heroLink.label), React.createElement(ArrowRight, {
    ml: 2,
    color: 'text.lightest'
  }))))), heroLink && React.createElement(ClickableOverlay, {
    onClick: () => {
      handleSelect(heroLink.analyticsTracking);
      window.open(heroLink.url, heroLink.openInNewTab ? '_blank' : '_self');
    },
    "aria-label": heroHeading || heroLink.ariaLabel
  })), React.createElement(Box, {
    mt: heroImage ? ['-12%', null, '-8%', null, '-7%', '-5%'] : 0
  }, React.createElement(Box, {
    overflow: 'visible'
  }, React.createElement(Carousel, {
    visibleSlides: [2.5, 3.5, 6],
    mobileVisibleSlides: [2.2, 2.5],
    overflowAllowancePxX: overflowAllowancePxX
  }, collection.map((item, index) => React.createElement(HeroItem, {
    key: `${item.heading}-${index}`,
    imageSrc: `${item.image?.asset?.file?.url}?w=374&fit=fill&q=80&fm=webp`,
    alt: item.image?.altText,
    heading: item.heading,
    paragraph: item.paragraph,
    href: item.link?.url,
    overlayText: item?.overlayText,
    onClick: () => {
      handleSelect(item.link?.analyticsTracking);
    },
    showAdHighlight: item.showAdHighlight
  }))))), collectionLink && !isDesktop && isMounted && React.createElement(Link, {
    "data-testid": 'bottom-collection-link',
    variation: 'subtle',
    href: collectionLink.url,
    target: collectionLink.openInNewTab ? '_blank' : '_self',
    onClick: () => {
      handleSelect(collectionLink.analyticsTracking);
    },
    "aria-label": collectionLink.ariaLabel
  }, React.createElement(Text, {
    bold: true,
    fontSize: 1,
    color: 'primary'
  }, collectionLink.label)));
}